import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../../components/toast/ErrorToast";
import TwoDecimalsMoneyRenderer from "../../../../utils/TwoDecimalsMoneyRenderer";
import {CURRENT_ACCOUNTS_PATH} from "../../../../routes";
import AccountingDocumentNumber from "../../AccountingDocumentNumber";

const NOT_DUE_TIER_CODE = 'not_due';

export default class PortfolioAgingFeature {

    constructor(application, navigator) {
        this._application = application;
        this._navigate = navigator;
        this._loaded = false;
        this._topDebtorsTableOpen = true;
        this._topDebtorsLoading = false;
        this._tiers = [];
        this._total = null;
        this._selectedTierCode = NOT_DUE_TIER_CODE;
        this._topDebtors = null;
        this._topDebtorsByTierCode = {};

        makeObservable(this, {
            _loaded: observable,
            _topDebtorsTableOpen: observable,
            _topDebtorsLoading: observable,
            _tiers: observable,
            _total: observable,
            _selectedTierCode: observable,
            _topDebtors: observable,
            loaded: computed,
            topDebtorsTableOpen: computed,
            topDebtorsLoading: computed,
            tiers: computed,
            total: computed,
            selectedTierCode: computed,
            topDebtors: computed,
            toggleTopDebtorsTable: action,
            selectTier: action,
        })
    }

    async load() {
        await Promise.all([this._getPortfolioAging(), this._getTopDebtorsOf(this._selectedTierCode)])
        this._finishLoading()
    }

    _finishLoading() {
        this._loaded = true
    }

    get loaded() {
        return this._loaded;
    }

    get topDebtorsTableOpen() {
        return this._topDebtorsTableOpen;
    }

    toggleTopDebtorsTable() {
        this._topDebtorsTableOpen = !this._topDebtorsTableOpen;
    }

    get topDebtorsLoading() {
        return this._topDebtorsLoading;
    }

    get tiers() {
        return this._tiers;
    }

    get total() {
        return this._total;
    }

    get selectedTierCode() {
        return this._selectedTierCode;
    }

    get topDebtors() {
        return this._topDebtors;
    }

    async selectTier(tierCode) {
        this._selectedTierCode = tierCode;
        await this._getTopDebtorsOf(tierCode);
    }

    biggestTierAmount() {
        return Math.max(...this.tiers.map(tier => tier.total.amount));
    }

    presentedTiers() {
        const biggestAmount = this.biggestTierAmount();
        return this.tiers.map(tier => ({
            code: tier.code,
            label: tier.label,
            amount: TwoDecimalsMoneyRenderer(tier.total.amount),
            barPercentage: biggestAmount > 0 ? (tier.total.amount / biggestAmount) * 100 : 0,
            stackedPercentage: this.total.amount > 0 ? (tier.total.amount / this.total.amount) * 100 : 0,
            selected: tier.code === this.selectedTierCode,
        }));
    }

    presentedTotal() {
        return TwoDecimalsMoneyRenderer(this.total.amount);
    }

    presentedTopDebtors() {
        if (!this.topDebtors) {
            return [];
        }
        return this.topDebtors.debtors.map(debtor => ({
            position: debtor.position,
            businessName: debtor.businessName,
            fantasyName: debtor.fantasyName,
            channel: debtor.channel,
            amount: TwoDecimalsMoneyRenderer(debtor.amount.amount),
            agedDebts: debtor.agedDebts.map(agedDebt => ({
                label: this._agedDebtLabelOf(agedDebt),
                issueDate: agedDebt.issueDate,
                pendingAmount: TwoDecimalsMoneyRenderer(agedDebt.pendingAmount.amount),
            })),
        }));
    }

    _agedDebtLabelOf(agedDebt) {
        const isInitialBalanceRemainder = agedDebt.number === null;
        if (isInitialBalanceRemainder) {
            return 'Saldo inicial';
        }
        return `${agedDebt.type} ${new AccountingDocumentNumber(agedDebt.pointOfSale, agedDebt.number).shortened()}`;
    }

    selectedTierLabel() {
        const selectedTier = this.tiers.find(tier => tier.code === this.selectedTierCode);
        return selectedTier ? selectedTier.label : '';
    }

    selectedTierCustomersCount() {
        return this.topDebtors ? this.topDebtors.customersCount : 0;
    }

    selectedTierTotal() {
        return this.topDebtors ? TwoDecimalsMoneyRenderer(this.topDebtors.tierTotal.amount) : TwoDecimalsMoneyRenderer(0);
    }

    navigateToCurrentAccountScreen(businessName) {
        this._navigate(`${CURRENT_ACCOUNTS_PATH}/${encodeURIComponent(businessName)}`);
    }

    async _getPortfolioAging() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._tiers = response.tiers();
                this._total = response.total();
            },
            handlesError: () => {
                errorToast('No se pudo obtener el aging de la cartera');
            }
        });
        return this._application.apiClient().getPortfolioAging(responseHandler);
    }

    async _getTopDebtorsOf(tierCode) {
        const cachedTopDebtors = this._topDebtorsByTierCode[tierCode];
        if (cachedTopDebtors) {
            this._topDebtors = cachedTopDebtors;
            return;
        }

        this._topDebtorsLoading = true;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                const topDebtors = {
                    tier: response.tier(),
                    customersCount: response.customersCount(),
                    tierTotal: response.tierTotal(),
                    debtors: response.debtors(),
                };
                this._topDebtorsByTierCode[tierCode] = topDebtors;
                this._topDebtors = topDebtors;
                this._topDebtorsLoading = false;
            },
            handlesError: () => {
                this._topDebtorsLoading = false;
                errorToast('No se pudieron obtener los principales deudores del tramo');
            }
        });
        return this._application.apiClient().getPortfolioAgingTopDebtors(tierCode, responseHandler);
    }

}
